import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Pencil, Search, Ticket, Type, X } from 'lucide-react';
import { IconBulb, IconMapPin, IconMapPinCheck, IconMapPinX } from '@tabler/icons-react';
import {
  useComposerVoiceRecorder,
  VoiceMicButton,
} from './ComposerVoiceRecorder';
import {
  checkOpenOS,
  checkRetiradaAtivaAbertura,
  searchEquipment,
} from '../../new-os/services/equipmentService';
import { searchDefeitos } from '../../new-os/services/defeitoService';
import { createOS, getStatusCode } from '../../new-os/services/osService';
import { searchAddress } from '../../new-os/services/addressService';
import { formatAddress } from '../../new-os/utils/formatters';
import { formatDuration } from '../utils/boardChatAttachmentUtils';
import '../../process-config/components/DefeitosVerveRichTextContent.css';

function norm(value) {
  return String(value || '').trim().toUpperCase();
}

function digits(value) {
  return String(value || '').replace(/\D/g, '');
}

function userData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || 'null') || {};
  } catch {
    return {};
  }
}

function firstNameOrEmail(email, fallback = '') {
  const value = String(email || fallback || '').trim();
  return value.includes('@') ? value.split('@')[0] : value;
}

function equipmentTitle(equipment) {
  if (!equipment) return '';
  return [equipment.serie, equipment.patrimonio, equipment.produto].filter(Boolean).join(' · ');
}

function addressText(equipment) {
  if (!equipment) return '';
  const main = equipment.Endereco || [
    equipment.end_logradouro,
    equipment.end_num,
    equipment.end_bairro,
    equipment.end_cidade ? `${equipment.end_cidade} - ${equipment.end_estado || ''}` : '',
    equipment.end_cep,
  ].filter(Boolean).join(', ');
  return [main, equipment.Endereco_Local ? `(${equipment.Endereco_Local})` : '']
    .filter(Boolean)
    .join(' ');
}

function buildAddressSuggestion(item, query) {
  const address = item.address || {};
  const formatted = formatAddress(item, query);
  let display = formatted || item.display_name || '';
  const numberMatch = String(query || '').match(/\b(\d+[A-Za-z]?|S\/N|s\/n)\b/i);
  let extractedNumber = null;

  if (numberMatch && !display.includes(numberMatch[1])) {
    const road = address.road || address.street || '';
    if (road) {
      display = display.replace(road, `${road}, ${numberMatch[1]}`);
      extractedNumber = numberMatch[1].toUpperCase() === 'S/N' ? '0' : numberMatch[1];
    }
  }

  if (!extractedNumber) {
    extractedNumber = address.house_number || address.house_name || null;
    if (extractedNumber && extractedNumber.toUpperCase() === 'S/N') extractedNumber = '0';
  }

  return {
    display,
    address,
    lat: item.lat,
    lon: item.lon,
    original: item.display_name,
    extractedNumber,
  };
}

function correctedAddressFields(suggestion, query) {
  const address = suggestion.address || {};
  let houseNumber = suggestion.extractedNumber || address.house_number || address.house_name || null;

  if (houseNumber && houseNumber.toUpperCase?.() === 'S/N') houseNumber = '0';
  if (!houseNumber && suggestion.display) {
    const match = suggestion.display.match(/,\s*(\d+[A-Za-z]?|S\/N|s\/n)\b/i);
    if (match) houseNumber = match[1].toUpperCase() === 'S/N' ? '0' : match[1];
  }
  if (!houseNumber && suggestion.original) {
    const match = suggestion.original.match(/,\s*(\d+[A-Za-z]?|S\/N|s\/n)\b/i);
    if (match) houseNumber = match[1].toUpperCase() === 'S/N' ? '0' : match[1];
  }
  if (!houseNumber && query) {
    const match = query.match(/\b(\d+[A-Za-z]?|S\/N|s\/n)\b/i);
    if (match) houseNumber = match[1].toUpperCase() === 'S/N' ? '0' : match[1];
  }

  return {
    corrected_end_logradouro: address.road || address.street || address.pedestrian || '',
    corrected_end_num: houseNumber || '0',
    corrected_end_comp: address.unit || address.level || '',
    corrected_end_bairro:
      address.suburb || address.neighbourhood || address.quarter || address.city_district || '',
    corrected_end_cidade: address.city || address.town || address.village || address.municipality || '',
    corrected_end_estado: address.state ? address.state.substring(0, 2).toUpperCase() : '',
    corrected_end_cep: address.postcode || '',
  };
}

function errorMessage(result, fallback) {
  const first = Array.isArray(result?.errors) ? result.errors[0] : null;
  return first?.ErrorMessage || result?.message || fallback;
}

function openGoogleMaps(address) {
  const query = String(address || '').trim();
  if (!query) return;
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
    '_blank',
    'noopener,noreferrer'
  );
}

const STEPS = [
  { id: 'equipment', label: 'Equipamento' },
  { id: 'address', label: 'Endereço' },
  { id: 'defect', label: 'Defeito' },
  { id: 'result', label: 'Resultado' },
];

function defectSuggestionHtml(defect) {
  return String(defect?.auto_solution_suggestions || '').trim();
}

function SolutionReadOnlyModal({ defect, onClose }) {
  const raw = defectSuggestionHtml(defect);
  if (!raw) return null;
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(raw);

  return createPortal(
    <div className="bch-os-solution-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="bch-os-solution-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bch-os-solution-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="bch-os-solution-head">
          <h3 id="bch-os-solution-title">{defect.nome || 'Sugestão de solução'}</h3>
          <button type="button" onClick={onClose} aria-label="Fechar sugestão de solução">
            <X size={16} strokeWidth={1.8} />
          </button>
        </header>
        <div className="bch-os-solution-scroll">
          {isHtml ? (
            <div
              className="bch-os-solution-content defeitos-vv-rt-editor"
              dangerouslySetInnerHTML={{ __html: raw }}
            />
          ) : (
            <div className="bch-os-solution-content bch-os-solution-content--plain">
              {raw}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function BoardChatOpenOsPanel({
  className = '',
  contracts = [],
  portalCustomer,
  contactName,
  onCancel,
  onCreated,
}) {
  const contractSet = useMemo(
    () => new Set((contracts || []).map((c) => norm(c)).filter(Boolean)),
    [contracts]
  );
  const contractLabel = (contracts || []).filter(Boolean).join(', ');

  const [equipmentQuery, setEquipmentQuery] = useState('');
  const [equipmentSuggestions, setEquipmentSuggestions] = useState([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [addressIncorrect, setAddressIncorrect] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [correctedAddress, setCorrectedAddress] = useState(null);
  const [internalLocationDraft, setInternalLocationDraft] = useState('');
  const [internalLocationEditing, setInternalLocationEditing] = useState(false);
  const [internalLocationConfirmed, setInternalLocationConfirmed] = useState(false);
  const [noInternalLocation, setNoInternalLocation] = useState(false);
  const [defectQuery, setDefectQuery] = useState('');
  const [defectSuggestions, setDefectSuggestions] = useState([]);
  const [defectLoading, setDefectLoading] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [observation, setObservation] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState('equipment');
  const [solutionModalOpen, setSolutionModalOpen] = useState(false);
  const [pendingEquipment, setPendingEquipment] = useState(null);
  const [equipmentGuardLoading, setEquipmentGuardLoading] = useState(false);
  const [showOpenOsWarning, setShowOpenOsWarning] = useState(false);
  const [openOsNumbers, setOpenOsNumbers] = useState([]);
  const [showRetiradaWarning, setShowRetiradaWarning] = useState(false);
  const [retiradaCodigos, setRetiradaCodigos] = useState([]);
  const defectSearchInputRef = useRef(null);
  const voiceRecorder = useComposerVoiceRecorder({
    onError: (msg) => setError(msg || 'Falha no microfone'),
  });

  const canSearchEquipment = contractSet.size > 0;
  const equipmentDone = Boolean(selectedEquipment);
  const baseAddressDone = equipmentDone && (addressConfirmed || (addressIncorrect && correctedAddress));
  const addressDone = Boolean(baseAddressDone && internalLocationConfirmed);
  const defectDone = Boolean(selectedDefect && observation.trim().length >= 15);
  const resultDone = Boolean(result?.success);
  const allDone = equipmentDone && addressDone && defectDone;

  useEffect(() => {
    const q = norm(equipmentQuery);
    if (!canSearchEquipment || q.length < 4) {
      setEquipmentSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setEquipmentLoading(true);
      setError('');
      try {
        const response = await searchEquipment(q.slice(0, 4), 'contrato', true);
        const all = Array.isArray(response.suggestions) ? response.suggestions : [];
        const filtered = all
          .filter((item) => {
            const matchesContract = contractSet.has(norm(item.codigo));
            const serie = norm(item.serie);
            const patrimonio = norm(item.patrimonio);
            return matchesContract && (serie.includes(q) || patrimonio.includes(q));
          })
          .slice(0, 8);
        setEquipmentSuggestions(filtered);
      } catch {
        setEquipmentSuggestions([]);
        setError('Não foi possível buscar equipamentos.');
      } finally {
        setEquipmentLoading(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [canSearchEquipment, contractSet, equipmentQuery]);

  useEffect(() => {
    const q = addressQuery.trim();
    if (!addressIncorrect || q.length < 3 || correctedAddress) {
      setAddressSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const res = await searchAddress(q);
        const suggestions = res.success
          ? (res.items || []).map((item) => buildAddressSuggestion(item, q))
          : [];
        setAddressSuggestions(suggestions);
      } catch {
        setAddressSuggestions([]);
      } finally {
        setAddressLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [addressIncorrect, addressQuery, correctedAddress]);

  useEffect(() => {
    const q = defectQuery.trim();
    if (!selectedEquipment || q.length < 2) {
      setDefectSuggestions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setDefectLoading(true);
      try {
        const res = await searchDefeitos(q, selectedEquipment.codprod);
        setDefectSuggestions(Array.isArray(res.items) ? res.items.slice(0, 8) : []);
      } catch {
        setDefectSuggestions([]);
      } finally {
        setDefectLoading(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [defectQuery, selectedEquipment]);

  useEffect(() => {
    if (activeStep !== 'defect' || !addressDone || selectedDefect || resultDone) return undefined;
    const timer = setTimeout(() => defectSearchInputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, [activeStep, addressDone, selectedDefect, resultDone]);

  const clearEquipmentGuard = () => {
    setPendingEquipment(null);
    setEquipmentGuardLoading(false);
    setShowOpenOsWarning(false);
    setOpenOsNumbers([]);
    setShowRetiradaWarning(false);
    setRetiradaCodigos([]);
  };

  const applySelectedEquipment = (item) => {
    setSelectedEquipment(item);
    setEquipmentQuery(item.serie || item.patrimonio || '');
    setEquipmentSuggestions([]);
    clearEquipmentGuard();
    setAddressConfirmed(false);
    setAddressIncorrect(false);
    setAddressQuery('');
    setAddressSuggestions([]);
    setCorrectedAddress(null);
    setInternalLocationDraft(item.Endereco_Local || '');
    setInternalLocationEditing(false);
    setInternalLocationConfirmed(false);
    setNoInternalLocation(false);
    setDefectQuery('');
    setSelectedDefect(null);
    setObservation('');
    setResult(null);
    setError('');
    setActiveStep('address');
  };

  const pickEquipment = async (item) => {
    if (!item?.serie) {
      applySelectedEquipment(item);
      return;
    }

    setEquipmentGuardLoading(true);
    setError('');
    try {
      const [openResult, retiradaResult] = await Promise.all([
        checkOpenOS(item.serie),
        checkRetiradaAtivaAbertura(item.serie),
      ]);

      const hasOpen = openResult.success && openResult.hasOpenOs;
      const hasRetirada =
        retiradaResult.success &&
        retiradaResult.hasRetiradaAtiva &&
        Array.isArray(retiradaResult.codigos) &&
        retiradaResult.codigos.length > 0;

      if (hasOpen || hasRetirada) {
        setPendingEquipment(item);
        if (hasOpen) {
          setOpenOsNumbers(openResult.osNumbers || []);
          setShowOpenOsWarning(true);
        } else {
          setOpenOsNumbers([]);
          setShowOpenOsWarning(false);
        }
        if (hasRetirada) {
          setRetiradaCodigos(retiradaResult.codigos || []);
          setShowRetiradaWarning(true);
        } else {
          setRetiradaCodigos([]);
          setShowRetiradaWarning(false);
        }
        return;
      }

      applySelectedEquipment(item);
    } catch {
      applySelectedEquipment(item);
    } finally {
      setEquipmentGuardLoading(false);
    }
  };

  useEffect(() => {
    if (!pendingEquipment) return;
    if (showOpenOsWarning || showRetiradaWarning) return;
    const eq = pendingEquipment;
    setPendingEquipment(null);
    applySelectedEquipment(eq);
  }, [pendingEquipment, showOpenOsWarning, showRetiradaWarning]);

  const pickDefect = (item) => {
    setSelectedDefect(item);
    setDefectQuery(item.nome || '');
    setDefectSuggestions([]);
    setResult(null);
    setSolutionModalOpen(false);
  };

  const toggleStep = (stepId) => {
    if (stepId === 'address' && !equipmentDone) return;
    if (stepId === 'defect' && !addressDone) return;
    setActiveStep((current) => (current === stepId ? '' : stepId));
  };

  const confirmAddress = (status) => {
    if (status === 'correct') {
      setAddressConfirmed(true);
      setAddressIncorrect(false);
      setAddressQuery('');
      setAddressSuggestions([]);
      setCorrectedAddress(null);
      setInternalLocationConfirmed(false);
      setNoInternalLocation(false);
      setActiveStep('address');
    } else {
      setAddressConfirmed(false);
      setAddressIncorrect(true);
      setAddressQuery('');
      setAddressSuggestions([]);
      setCorrectedAddress(null);
      setInternalLocationConfirmed(false);
      setNoInternalLocation(false);
    }
    setResult(null);
  };

  const pickAddress = (suggestion) => {
    const updatedEquipment = {
      ...selectedEquipment,
      Endereco: String(suggestion.display || '').toUpperCase(),
      ...correctedAddressFields(suggestion, addressQuery),
    };
    setSelectedEquipment(updatedEquipment);
    setCorrectedAddress(suggestion);
    setAddressQuery('');
    setAddressSuggestions([]);
    setInternalLocationConfirmed(false);
    setNoInternalLocation(false);
    setResult(null);
    setActiveStep('address');
  };

  const confirmInternalLocation = () => {
    const value = internalLocationDraft.trim();
    setSelectedEquipment((prev) => (prev ? { ...prev, Endereco_Local: value } : prev));
    setInternalLocationConfirmed(true);
    setNoInternalLocation(!value);
    setInternalLocationEditing(false);
    setResult(null);
    setActiveStep('defect');
  };

  const markNoInternalLocation = () => {
    setSelectedEquipment((prev) => (prev ? { ...prev, Endereco_Local: '' } : prev));
    setInternalLocationDraft('');
    setInternalLocationConfirmed(true);
    setNoInternalLocation(true);
    setInternalLocationEditing(false);
    setResult(null);
    setActiveStep('defect');
  };

  const handleObservationTranscribe = async () => {
    setError('');
    try {
      if (!voiceRecorder.speechSupported) {
        await voiceRecorder.cancel();
        setError('Não foi possível obter o texto desta gravação.');
        return;
      }
      const text = String((await voiceRecorder.finishTranscript()) || '').trim();
      if (!text) {
        setError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setObservation((prev) => (prev ? `${prev.trim()} ${text}` : text));
    } catch (e) {
      setError(e.message || 'Não foi possível obter o texto desta gravação.');
    }
  };

  const handleCreate = async () => {
    if (!selectedEquipment || !addressDone || !defectDone || creating) return;
    setCreating(true);
    setError('');
    setResult(null);
    try {
      const u = userData();
      const userEmail = u.email || '';
      const userName = firstNameOrEmail(userEmail, u.username || u.name || '');
      const status = await getStatusCode(null, 'contrato', 'N');
      if (!status?.success || !status.codStatus) {
        throw new Error('Não foi possível obter o status para abertura da OS.');
      }
      const hasCorrectedAddress = Boolean(selectedEquipment.corrected_end_logradouro);

      const osPayload = {
        NOME_USUARIO: userName,
        CONTRATO: selectedEquipment.codigo || '',
        CODCLI: selectedEquipment.codCli || '',
        CODPROD: selectedEquipment.codprod || '',
        SERIE: selectedEquipment.serie || '',
        ATENDENTE: userName,
        SOLICITANTE: portalCustomer?.nomeCliente || contactName || 'Cliente',
        CODEMP: selectedEquipment.codEmp || '',
        MOTIVO_OBS_DEFEITO: observation.trim(),
        CODTEC: selectedEquipment.codTec || null,
        CONTADOR_PB: 0,
        CONTADOR_COR: 0,
        CONTADOR_TOTAL: 0,
        END_LOGRADOURO: hasCorrectedAddress
          ? selectedEquipment.corrected_end_logradouro || ''
          : selectedEquipment.end_logradouro || '',
        END_NUM: hasCorrectedAddress
          ? selectedEquipment.corrected_end_num || '0'
          : selectedEquipment.end_num || '0',
        END_LOGRADOURO_COMP: hasCorrectedAddress
          ? selectedEquipment.corrected_end_comp || ''
          : selectedEquipment.end_comp || '',
        END_BAIRRO: hasCorrectedAddress
          ? selectedEquipment.corrected_end_bairro || ''
          : selectedEquipment.end_bairro || '',
        END_CIDADE: hasCorrectedAddress
          ? selectedEquipment.corrected_end_cidade || ''
          : selectedEquipment.end_cidade || '',
        END_ESTADO: hasCorrectedAddress
          ? selectedEquipment.corrected_end_estado || ''
          : selectedEquipment.end_estado || '',
        END_CEP: hasCorrectedAddress
          ? selectedEquipment.corrected_end_cep || ''
          : selectedEquipment.end_cep || '',
        FONE: digits(portalCustomer?.foneFixo || portalCustomer?.foneWhatsapp || selectedEquipment.end_fone),
        EMAIL: portalCustomer?.email || selectedEquipment.end_email || null,
        CODSITE: selectedEquipment.codSite || '',
        CONTATO: digits(portalCustomer?.foneFixo || portalCustomer?.foneWhatsapp || ''),
        CONTATO_FONE1: digits(portalCustomer?.foneFixo || ''),
        CONTATO_WHATSAPP: digits(portalCustomer?.foneWhatsapp || ''),
        CONTATO_FONE2: '',
        LOCALIZACAO: selectedEquipment.Endereco_Local || '',
        CODIGO_DEFEITO: selectedDefect?.codigo || '',
        CODSTATUS: status.codStatus,
        ORIGEM: 'C',
        TIPOOS: 'N',
        FONE2: '',
        FONEWHATS: digits(portalCustomer?.foneWhatsapp || ''),
        userId: u.id || null,
      };

      const created = await createOS(osPayload);
      if (!created?.success) {
        throw new Error(errorMessage(created, 'Erro ao gerar ordem de serviço.'));
      }
      setResult(created);
      setActiveStep('');
      onCreated?.({
        osNumber: created.osNumber,
        equipment: selectedEquipment,
        defect: selectedDefect,
      });
    } catch (e) {
      setResult({ success: false, message: e.message });
      setError(e.message || 'Erro ao gerar ordem de serviço.');
    } finally {
      setCreating(false);
    }
  };

  const stepMeta = {
    equipment: {
      number: 1,
      done: equipmentDone,
      unlocked: true,
      active: activeStep === 'equipment',
      summary: selectedEquipment || null,
    },
    address: {
      number: 2,
      done: addressDone,
      unlocked: equipmentDone,
      active: activeStep === 'address',
      summary: addressDone ? addressText(selectedEquipment) || 'Endereço confirmado' : '',
    },
    defect: {
      number: 3,
      done: defectDone,
      unlocked: addressDone,
      active: activeStep === 'defect',
      summary: defectDone ? selectedDefect?.nome || '' : '',
    },
    result: {
      number: 4,
      done: resultDone,
      unlocked: defectDone,
      active: false,
      summary: resultDone ? `OS ${result.osNumber || ''}` : '',
    },
  };

  const progressSteps = STEPS.map((step) => ({ ...step, ...stepMeta[step.id] }));

  const renderStepIcon = (stepId) => (
    <span className={`bch-open-os-step-icon${stepMeta[stepId].done ? ' is-done' : ''}`}>
      {stepMeta[stepId].done ? (
        <>
          <Check className="bch-open-os-step-check" size={12} strokeWidth={2.5} />
          <Pencil className="bch-open-os-step-edit" size={12} strokeWidth={2.2} />
        </>
      ) : (
        stepMeta[stepId].number
      )}
    </span>
  );

  return (
    <section className={`bch-card bch-card--open-os ${className}`.trim()}>
      <div className="bch-card-head bch-open-os-head">
        <h2 className="bch-card-title">Abrir ordem de serviço</h2>
        {resultDone ? (
          <button type="button" className="bch-open-os-close" onClick={onCancel}>
            Confirmar
          </button>
        ) : (
          <button type="button" className="bch-open-os-cancel" onClick={onCancel} aria-label="Cancelar abertura de OS">
            <X size={16} strokeWidth={1.8} />
          </button>
        )}
      </div>

      <div className="bch-open-os-progress" aria-label="Etapas de abertura de OS">
        {progressSteps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className={`bch-open-os-progress-item${step.done ? ' is-done' : ''}${step.active ? ' is-active' : ''}`}>
              <span className="bch-open-os-progress-dot">
                {step.done ? <Check size={11} strokeWidth={2.5} /> : step.number}
              </span>
              <span className="bch-open-os-progress-label">{step.label}</span>
            </div>
            {index < progressSteps.length - 1 ? (
              <span className={`bch-open-os-progress-line${step.done ? ' is-done' : ''}`} />
            ) : null}
          </React.Fragment>
        ))}
      </div>

      <div className="bch-open-os-scroll" id="os-panel-scroll">
        <div className="bch-open-os-flow">
          <div className="bch-open-os-step-block">
            <button
              type="button"
              className="bch-open-os-step-head"
              onClick={() => toggleStep('equipment')}
              disabled={resultDone}
            >
              {renderStepIcon('equipment')}
              <span className="bch-open-os-step-text">
                <strong>Equipamento</strong>
                {selectedEquipment ? (
                  <small className="bch-open-os-step-summary-stack">
                    <span>{selectedEquipment.produto || 'Equipamento'}</span>
                    <span>
                      Série {selectedEquipment.serie || '-'} · Patrimônio {selectedEquipment.patrimonio || '-'}
                    </span>
                  </small>
                ) : null}
              </span>
              {equipmentDone ? (
                <ChevronDown className={`bch-open-os-chevron${activeStep === 'equipment' ? ' is-open' : ''}`} size={16} strokeWidth={2} />
              ) : null}
            </button>
            {activeStep === 'equipment' ? (
              <div className="bch-open-os-step-body">
                {selectedEquipment ? (
                  <div className="bch-open-os-selected-card">
                    <span>
                      <strong>{selectedEquipment.produto || equipmentTitle(selectedEquipment)}</strong>
                      <small>
                        Série {selectedEquipment.serie || '-'} · Patrimônio {selectedEquipment.patrimonio || '-'} · Contrato {selectedEquipment.codigo || '-'}
                      </small>
                    </span>
                    <button type="button" onClick={() => {
                      setSelectedEquipment(null);
                      setEquipmentQuery('');
                      clearEquipmentGuard();
                      setAddressConfirmed(false);
                      setAddressIncorrect(false);
                      setAddressQuery('');
                      setAddressSuggestions([]);
                      setCorrectedAddress(null);
                      setInternalLocationDraft('');
                      setInternalLocationEditing(false);
                      setInternalLocationConfirmed(false);
                      setNoInternalLocation(false);
                      setActiveStep('equipment');
                    }}>
                      Trocar
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="bch-open-os-muted">
                      Busca limitada aos contratos vinculados: {contractLabel || 'nenhum contrato encontrado'}.
                    </p>
                    <div className="bch-open-os-search">
                      <Search size={15} strokeWidth={1.8} />
                      <input
                        type="text"
                        value={equipmentQuery}
                        onChange={(e) => {
                          setEquipmentQuery(e.target.value);
                          setSelectedEquipment(null);
                          clearEquipmentGuard();
                          setAddressConfirmed(false);
                          setAddressIncorrect(false);
                          setAddressQuery('');
                          setAddressSuggestions([]);
                          setCorrectedAddress(null);
                          setInternalLocationDraft('');
                          setInternalLocationEditing(false);
                          setInternalLocationConfirmed(false);
                          setNoInternalLocation(false);
                          setResult(null);
                        }}
                        placeholder="Buscar por série, patrimônio ou modelo"
                        disabled={!canSearchEquipment || resultDone || equipmentGuardLoading}
                      />
                    </div>
                    {!canSearchEquipment ? (
                      <p className="bch-open-os-error">Cliente sem contratos vinculados para buscar equipamentos.</p>
                    ) : null}
                    {equipmentLoading || equipmentGuardLoading ? (
                      <p className="bch-open-os-hint">
                        {equipmentGuardLoading ? 'Validando equipamento...' : 'Buscando equipamentos...'}
                      </p>
                    ) : null}
                    {showOpenOsWarning ? (
                      <div className="bch-open-os-guard-alert" role="alert">
                        <div className="bch-open-os-guard-alert-body">
                          <strong>Ordem(ns) de serviço em aberto</strong>
                          <p>
                            Já existe(m) ordem(ns) de serviço em aberto para este equipamento:{' '}
                            <span className="bch-open-os-guard-codes">
                              {(openOsNumbers || []).map((num) => (
                                <em key={String(num)}>{num}</em>
                              ))}
                            </span>
                          </p>
                          <small>Não é possível prosseguir com a abertura de nova OS.</small>
                        </div>
                        <div className="bch-open-os-guard-alert-actions">
                          <button
                            type="button"
                            className="bch-open-os-guard-continue"
                            onClick={() => {
                              setShowOpenOsWarning(false);
                              setOpenOsNumbers([]);
                            }}
                          >
                            Desejo continuar
                          </button>
                          <button
                            type="button"
                            className="bch-open-os-guard-dismiss"
                            aria-label="Fechar aviso de OS em aberto"
                            onClick={() => {
                              setShowOpenOsWarning(false);
                              setOpenOsNumbers([]);
                              if (!showRetiradaWarning) setPendingEquipment(null);
                            }}
                          >
                            <X size={14} strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {showRetiradaWarning ? (
                      <div className="bch-open-os-guard-alert" role="alert">
                        <div className="bch-open-os-guard-alert-body">
                          <strong>Este equipamento possui retirada ativa em andamento</strong>
                          <p>
                            Código da retirada:{' '}
                            <span className="bch-open-os-guard-codes">
                              {(retiradaCodigos || []).map((cod) => (
                                <em key={String(cod)}>{cod}</em>
                              ))}
                            </span>
                          </p>
                          <small>Não é possível prosseguir com a abertura de nova OS sem confirmar.</small>
                        </div>
                        <div className="bch-open-os-guard-alert-actions">
                          <button
                            type="button"
                            className="bch-open-os-guard-continue"
                            onClick={() => {
                              setShowRetiradaWarning(false);
                              setRetiradaCodigos([]);
                            }}
                          >
                            Desejo continuar
                          </button>
                          <button
                            type="button"
                            className="bch-open-os-guard-dismiss"
                            aria-label="Fechar aviso de retirada ativa"
                            onClick={() => {
                              setShowRetiradaWarning(false);
                              setRetiradaCodigos([]);
                              if (!showOpenOsWarning) setPendingEquipment(null);
                            }}
                          >
                            <X size={14} strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {equipmentSuggestions.length > 0 ? (
                      <div className="bch-open-os-suggestions">
                        {equipmentSuggestions.map((item) => (
                          <button
                            key={`${item.codigo}-${item.serie}-${item.patrimonio}`}
                            type="button"
                            onClick={() => pickEquipment(item)}
                            disabled={equipmentGuardLoading}
                          >
                            <strong>{item.produto || equipmentTitle(item)}</strong>
                            <span>Série {item.serie || '-'} · Patrimônio {item.patrimonio || '-'} · Contrato {item.codigo || '-'}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>

          <div className={`bch-open-os-step-block${!equipmentDone ? ' is-locked' : ''}`}>
            <button
              type="button"
              className="bch-open-os-step-head"
              onClick={() => toggleStep('address')}
              disabled={!equipmentDone || resultDone}
            >
              {renderStepIcon('address')}
              <span className="bch-open-os-step-text">
                <strong>Endereço</strong>
                {stepMeta.address.summary ? <small>{stepMeta.address.summary}</small> : null}
              </span>
              {addressDone ? (
                <ChevronDown className={`bch-open-os-chevron${activeStep === 'address' ? ' is-open' : ''}`} size={16} strokeWidth={2} />
              ) : null}
            </button>
            {activeStep === 'address' ? (
              <div className="bch-open-os-step-body">
                {!equipmentDone ? (
                  <p className="bch-open-os-locked-text">Conclua a etapa anterior para continuar.</p>
                ) : (
                  <>
                    <p className="bch-open-os-muted">Endereço identificado no cadastro do contrato.</p>
                    {addressIncorrect && !correctedAddress ? (
                      <div className="bch-open-os-address-search">
                        <input
                          type="text"
                          value={addressQuery}
                          onChange={(e) => setAddressQuery(e.target.value)}
                          placeholder="Pesquise o endereço correto aqui"
                          disabled={resultDone}
                        />
                        {addressLoading ? (
                          <p className="bch-open-os-hint">Buscando endereços...</p>
                        ) : null}
                        {addressSuggestions.length > 0 ? (
                          <div className="bch-open-os-suggestions bch-open-os-suggestions--address">
                            {addressSuggestions.map((item) => (
                              <button
                                key={`${item.lat}-${item.lon}-${item.display}`}
                                type="button"
                                onClick={() => pickAddress(item)}
                              >
                                <strong>{item.display}</strong>
                                <span>{item.original}</span>
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="bch-open-os-address-card">
                        <span>{addressText(selectedEquipment) || 'Endereço não informado no cadastro.'}</span>
                        {addressText(selectedEquipment) ? (
                          <button
                            type="button"
                            className="bch-open-os-maps-badge"
                            onClick={() => openGoogleMaps(addressText(selectedEquipment))}
                            aria-label="Abrir endereço no Google Maps"
                            data-bch-tooltip="Abrir no Google Maps"
                          >
                            <IconMapPin size={13} stroke={2} />
                          </button>
                        ) : null}
                      </div>
                    )}
                    <div className="bch-open-os-choice-row">
                      <button
                        type="button"
                        className={addressConfirmed ? 'is-selected' : ''}
                        onClick={() => confirmAddress('correct')}
                        disabled={resultDone}
                      >
                        <IconMapPinCheck size={15} stroke={1.9} aria-hidden />
                        Endereço correto
                      </button>
                      <button
                        type="button"
                        className={addressIncorrect ? 'is-selected' : ''}
                        onClick={() => confirmAddress('incorrect')}
                        disabled={resultDone}
                      >
                        <IconMapPinX size={15} stroke={1.9} aria-hidden />
                        Endereço incorreto
                      </button>
                    </div>
                    {baseAddressDone ? (
                      <div className="bch-open-os-internal">
                        <div className="bch-open-os-internal-head">
                          <strong>Local interno</strong>
                        </div>
                        {internalLocationEditing || !internalLocationConfirmed ? (
                          <div className="bch-open-os-internal-edit">
                            <div className="bch-open-os-internal-inline">
                              <div className="bch-open-os-internal-field">
                                <input
                                  type="text"
                                  value={internalLocationDraft}
                                  onChange={(e) => {
                                    setInternalLocationDraft(e.target.value);
                                    setInternalLocationConfirmed(false);
                                    setNoInternalLocation(false);
                                  }}
                                  placeholder="Informe a localização interna"
                                  disabled={resultDone || noInternalLocation}
                                />
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={noInternalLocation}
                                    disabled={resultDone}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        markNoInternalLocation();
                                      } else {
                                        setNoInternalLocation(false);
                                        setInternalLocationConfirmed(false);
                                      }
                                    }}
                                  />
                                  Não possui
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={confirmInternalLocation}
                                disabled={resultDone || !internalLocationDraft.trim()}
                              >
                                Confirmar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bch-open-os-internal-display">
                            <span>{noInternalLocation ? 'Não possui' : internalLocationDraft}</span>
                            <button
                              type="button"
                              className="bch-open-os-inline-edit"
                              onClick={() => {
                                setInternalLocationEditing(true);
                                setInternalLocationConfirmed(false);
                              }}
                              disabled={resultDone}
                            >
                              <Pencil size={13} strokeWidth={2} aria-hidden />
                              Editar
                            </button>
                            <button type="button" onClick={confirmInternalLocation} disabled={resultDone}>
                              Confirmar
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>

          <div className={`bch-open-os-step-block${!addressDone ? ' is-locked' : ''}`}>
            <button
              type="button"
              className="bch-open-os-step-head"
              onClick={() => toggleStep('defect')}
              disabled={!addressDone || resultDone}
            >
              {renderStepIcon('defect')}
              <span className="bch-open-os-step-text">
                <strong>Defeito</strong>
                {stepMeta.defect.summary ? <small>{stepMeta.defect.summary}</small> : null}
              </span>
              {defectDone ? (
                <ChevronDown className={`bch-open-os-chevron${activeStep === 'defect' ? ' is-open' : ''}`} size={16} strokeWidth={2} />
              ) : null}
            </button>
            {activeStep === 'defect' ? (
              <div className="bch-open-os-step-body">
                {!addressDone ? (
                  <p className="bch-open-os-locked-text">Conclua a etapa anterior para continuar.</p>
                ) : selectedDefect ? (
                  <>
                    <div className="bch-open-os-defect-selected-row">
                      <div className="bch-open-os-defect-pill">
                        <span>{selectedDefect.nome}</span>
                        <small>{selectedDefect.codigo}</small>
                        <button type="button" onClick={() => {
                          setSelectedDefect(null);
                          setObservation('');
                          setDefectQuery('');
                          setSolutionModalOpen(false);
                        }}>
                          ×
                        </button>
                      </div>
                      {defectSuggestionHtml(selectedDefect) ? (
                        <button
                          type="button"
                          className="bch-open-os-solution-trigger"
                          onClick={() => setSolutionModalOpen(true)}
                        >
                          <IconBulb size={14} stroke={1.9} aria-hidden />
                          Sugestão de solução
                        </button>
                      ) : null}
                    </div>
                    <div className="bch-open-os-observation-wrap">
                      <textarea
                        value={observation}
                        disabled={resultDone}
                        onChange={(e) => setObservation(e.target.value)}
                        placeholder="Adicione observações aqui... (obrigatório)"
                      />
                      <span className={`bch-open-os-observation-mic${resultDone ? ' is-disabled' : ''}`}>
                        <VoiceMicButton
                          recording={voiceRecorder.recording || voiceRecorder.busy}
                          tipProps={(label) => ({
                            'data-bch-tooltip': label,
                          })}
                          onClick={() => {
                            if (resultDone || voiceRecorder.busy) return;
                            voiceRecorder.toggle();
                          }}
                        />
                      </span>
                      {voiceRecorder.recording || voiceRecorder.busy ? (
                        <div className="bch-open-os-transcribe-pop" role="status" aria-live="polite">
                          <span className={`bch-voice-pulse${voiceRecorder.busy ? ' is-busy' : ''}`} aria-hidden />
                          <span className="bch-voice-timer">{formatDuration(voiceRecorder.elapsedSec)}</span>
                          <span className="bch-voice-label">
                            {voiceRecorder.busy ? 'Processando...' : 'Gravando...'}
                          </span>
                          <button
                            type="button"
                            className="bch-voice-action bch-voice-action--primary"
                            disabled={voiceRecorder.busy}
                            onClick={handleObservationTranscribe}
                          >
                            <Type size={14} strokeWidth={1.9} />
                            Transcrever
                          </button>
                          <button
                            type="button"
                            className="bch-voice-cancel"
                            disabled={voiceRecorder.busy}
                            aria-label="Cancelar gravação"
                            onClick={() => voiceRecorder.cancel()}
                          >
                            <X size={13} strokeWidth={2} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="bch-open-os-counter">
                      <span className={observation.trim().length >= 15 ? 'is-valid' : ''}>
                        {observation.trim().length >= 15
                          ? `✓ ${observation.trim().length} caracteres`
                          : `${observation.trim().length}/15 caracteres mínimos`}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bch-open-os-search">
                      <Search size={15} strokeWidth={1.8} />
                      <input
                        ref={defectSearchInputRef}
                        type="text"
                        value={defectQuery}
                        disabled={resultDone}
                        onChange={(e) => {
                          setDefectQuery(e.target.value);
                          setSelectedDefect(null);
                          setResult(null);
                        }}
                        placeholder="Buscar defeito..."
                      />
                    </div>
                    {defectLoading ? <p className="bch-open-os-hint">Buscando defeitos...</p> : null}
                    {defectSuggestions.length > 0 ? (
                      <div className="bch-open-os-suggestions">
                        {defectSuggestions.map((item) => (
                          <button key={item.codigo} type="button" onClick={() => pickDefect(item)}>
                            <strong>{item.nome}</strong>
                            <span>{item.codigo}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="bch-open-os-footer">
        {resultDone ? (
          <div className="bch-open-os-generated">
            <span className="bch-open-os-generated-icon">
              <Check size={16} strokeWidth={2.5} />
            </span>
            <span>
              <strong>Ordem de serviço gerada</strong>
              <small>Nº {result.osNumber || 'sem número retornado'}</small>
            </span>
          </div>
        ) : (
          <>
            {result && !result.success ? (
              <div className="bch-open-os-result is-error">{error || result.message}</div>
            ) : null}
            <button
              type="button"
              className="bch-open-os-submit"
              disabled={!allDone || creating}
              onClick={handleCreate}
            >
              <Ticket size={14} strokeWidth={1.8} aria-hidden />
              {creating ? 'Gerando...' : 'Gerar OS'}
            </button>
          </>
        )}
      </div>
      {solutionModalOpen ? (
        <SolutionReadOnlyModal defect={selectedDefect} onClose={() => setSolutionModalOpen(false)} />
      ) : null}
    </section>
  );
}
